import { type V2, TAU, angleTo, dist } from '../../core/math';
import { Rng } from '../../core/rng';
import { fbm2, polylineDistance } from '../../zones/build';
import { archway, disc, edgeRise, isOpen, lampsAlong, lifeNodes, openSpots, paint, place, placeFacing, ridge, ringOf } from '../../zones/townKit';
import type { ZoneLayout } from '../../zones/types';
import { ZoneBuilder } from '../../zones/ZoneBuilder';

/**
 * SUNSCAR EXPANSE (levels 12–21) — ruined desert canyons east of Emberwild.
 * Trails from Emberwild (west) and Havenreach (north-west) meet at the
 * caravan camp; the old caravan road runs east through a mesa-walled canyon
 * to Frosthollow. North of the canyon lie the colonnade ruins and the
 * Sandsworn Tomb; south lie the oasis and Dunemaw's sand arena. Levels climb
 * from the west (scorpions, raiders) through the ruins (skeletons) to the
 * golem-haunted east.
 */
export function buildSunscar(): ZoneLayout {
  const rng = new Rng('sunscar');
  const b = new ZoneBuilder(
    {
      id: 'sunscar', name: 'Sunscar Expanse', subtitle: 'Where the caravans vanish', kind: 'field', biome: 'desert',
      levelRange: [12, 21], music: 'desert', width: 240, depth: 240, seed: 3101,
      ground: ['sand', 'dirt', 'rock', 'dryGrass'], groundScale: [6, 5, 7, 5],
      hills: 2.4, hillScale: 0.02, slopeLayer: 2, patchLayer: 3, patchAmount: -0.06, waterLevel: -6, ambient: 'motes', mapTint: 0xa08050,
    },
    rng,
  );
  const PI = Math.PI;
  const W_EXIT: V2 = { x: -114, z: 24 };
  const NW_EXIT: V2 = { x: -88, z: -114 };
  const E_EXIT: V2 = { x: 114, z: -24 };
  const TOMB: V2 = { x: 52, z: -98 };
  const CAMP: V2 = { x: -62, z: -14 };
  const RUINS: V2 = { x: 28, z: -74 };
  const OASIS: V2 = { x: 2, z: 50 };
  const ARENA: V2 = { x: 46, z: 72 };
  const RAIDERS: V2 = { x: -54, z: 70 };

  // ---------------------------------------------------------------- terrain
  // Wind-combed dune ripples on top of the rolling base (pure arithmetic so
  // every peer carves identical dunes), canyon rim at the edges.
  b.addHeight((x, z) => {
    const u = (x * 0.6 - z * 0.8) * 0.0175 + fbm2(x * 0.012, z * 0.012, 91, 2) * 0.8;
    const t = Math.abs(2 * (u - Math.floor(u)) - 1);
    return 0.9 * (t * t * (3 - 2 * t) * 2 - 1);
  });
  edgeRise(b, 22, 14, [W_EXIT, NW_EXIT, E_EXIT], 20, 17);

  // Caravan trails.
  const toTown: V2[] = [{ x: -88, z: -120 }, { x: -87, z: -100 }, { x: -80, z: -76 }, { x: -70, z: -48 }, { x: -64, z: -28 }];
  const toVale: V2[] = [{ x: -120, z: 24 }, { x: -100, z: 22 }, { x: -86, z: 12 }, { x: -74, z: -2 }, { x: -66, z: -10 }];
  const caravanRd: V2[] = [{ x: -50, z: -14 }, { x: -26, z: -10 }, { x: 0, z: -4 }, { x: 28, z: -10 }, { x: 56, z: -18 }, { x: 84, z: -24 }, { x: 120, z: -24 }];
  const tombRd: V2[] = [{ x: 0, z: -4 }, { x: 8, z: -28 }, { x: 22, z: -52 }, { x: 38, z: -70 }, { x: 52, z: -88 }];
  const oasisRd: V2[] = [{ x: 0, z: -4 }, { x: -4, z: 18 }, { x: -2, z: 33 }];
  const arenaRd: V2[] = [{ x: 12, z: 34 }, { x: 26, z: 48 }, { x: 36, z: 60 }];
  const raidRd: V2[] = [{ x: -62, z: 0 }, { x: -58, z: 30 }, { x: -54, z: 56 }];
  b.road(toTown, 4.5, 1);
  b.road(toVale, 4.5, 1);
  b.road(caravanRd, 5, 1);
  b.road(tombRd, 4, 1);
  b.road(oasisRd, 4, 1);
  b.road(arenaRd, 3.5, 1);
  b.road(raidRd, 3.5, 1);

  // Mesas and canyon walls.
  const mesa = { width: 12, height: 9, soft: 6, layer: 2, tint: 0xc89868 };
  ridge(b, [{ x: -38, z: -122 }, { x: -34, z: -96 }, { x: -26, z: -72 }, { x: -14, z: -56 }], { ...mesa, seed: 1 });
  ridge(b, [{ x: 32, z: -36 }, { x: 52, z: -44 }, { x: 80, z: -52 }, { x: 100, z: -62 }], { ...mesa, height: 10, seed: 2 });
  ridge(b, [{ x: 44, z: 8 }, { x: 70, z: 4 }, { x: 96, z: 8 }, { x: 124, z: 14 }], { ...mesa, seed: 3 });
  ridge(b, [{ x: -38, z: 24 }, { x: -26, z: 42 }], { ...mesa, width: 10, height: 8, soft: 5, seed: 4 });
  ridge(b, [{ x: -26, z: 84 }, { x: -16, z: 104 }, { x: -12, z: 124 }], { ...mesa, seed: 5 });

  // Oasis: a basin below the water line, ringed by scrub.
  b.flat(OASIS, 8, { height: -9, soft: 8 });
  b.flat({ x: OASIS.x - 9, z: OASIS.z + 5 }, 5, { height: -8.5, soft: 6 });
  b.flat({ x: OASIS.x + 8, z: OASIS.z - 5 }, 4, { height: -8, soft: 5 });
  paint(b, 3, disc(OASIS, 22, 8), 3);

  // ---------------------------------------------------------------- exits & arrivals
  b.flat({ x: -104, z: 22 }, 8, { layer: 1 });
  b.portal({ id: 'ss_to_emberwild', to: 'emberwild', toSpawn: 'fromSunscar', x: W_EXIT.x, z: W_EXIT.z, radius: 3.5, label: 'Emberwild Vale', kind: 'exit' });
  b.spawn('fromEmberwild', -101, 22, PI / 2);
  b.flat({ x: -87, z: -102 }, 8, { layer: 1 });
  b.portal({ id: 'ss_to_town', to: 'havenreach', toSpawn: 'fromSunscar', x: NW_EXIT.x, z: NW_EXIT.z, radius: 3.5, label: 'Havenreach', kind: 'exit' });
  b.spawn('fromTown', -87, -100, 0);
  b.flat({ x: 104, z: -23 }, 8, { layer: 1 });
  b.portal({ id: 'ss_to_frosthollow', to: 'frosthollow', toSpawn: 'fromSunscar', x: E_EXIT.x, z: E_EXIT.z, radius: 3.5, label: 'Frosthollow', kind: 'exit', minLevel: 21 });
  b.spawn('fromFrosthollow', 101, -22, -PI / 2);
  for (const [p, rot] of [[{ x: -104, z: 16 }, 0.4], [{ x: -93, z: -103 }, 1.9], [{ x: 104, z: -30 }, -1.2]] as [V2, number][]) place(b, 'signpost', p.x, p.z, rot);
  for (const p of [{ x: -106, z: 28 }, { x: -106, z: 17 }, { x: -83, z: -106 }, { x: -92, z: -107 }, { x: 106, z: -18 }, { x: 106, z: -29 }]) place(b, 'brazier', p.x, p.z);

  // ---------------------------------------------------------------- caravan camp (hub)
  b.flat(CAMP, 15, { layer: 1 });
  const c = (dx: number, dz: number): V2 => ({ x: CAMP.x + dx, z: CAMP.z + dz });
  b.spawn('start', CAMP.x + 4, CAMP.z + 7, PI);
  place(b, 'campfire', CAMP.x, CAMP.z);
  const tents: [number, number, number, number][] = [
    [-9, -8, 0.3, 0xd8c8a0],
    [8, -9, -0.4, 0xb86a4a],
    [-11, 5, 1.3, 0x4a8a8a],
    [11, 8, -1.9, 0xd8c8a0],
  ];
  for (const [dx, dz, rot, tint] of tents) place(b, 'tent', CAMP.x + dx, CAMP.z + dz, rot, { tint, scale: 1.2 });
  const hadi = c(0, -5.5);
  b.npc('caravan_master_hadi', hadi.x, hadi.z, 0);
  const zahra = c(-7, 2.5);
  b.npc('nomad_seer_zahra', zahra.x, zahra.z, angleTo(zahra, CAMP));
  place(b, 'brazier', CAMP.x - 9.5, CAMP.z - 0.5);
  const stall = c(10.5, 0.5);
  placeFacing(b, 'market_stall', stall, CAMP, { tint: 0xc09060 });
  const farouk = c(7, 2.5);
  b.npc('trader_farouk', farouk.x, farouk.z, angleTo(farouk, CAMP));
  b.interact({ id: 'ss_waystone_camp', kind: 'waystone', x: CAMP.x - 1, z: CAMP.z + 9, data: { name: 'Caravan Camp' } });
  place(b, 'cart', CAMP.x - 3, CAMP.z - 12.5, 1.2);
  place(b, 'cart', CAMP.x + 13.5, CAMP.z - 4, 0.3);
  for (const [dx, dz] of [[5, -12], [6.2, -11.4], [-14, -2], [-13.5, 0.8], [13.5, 4], [14, 5.4]] as [number, number][]) {
    if (rng.chance(0.5)) place(b, 'crate', CAMP.x + dx, CAMP.z + dz, rng.range(0, PI));
    else place(b, 'barrel', CAMP.x + dx, CAMP.z + dz);
  }
  place(b, 'banner_pole', CAMP.x - 6, CAMP.z + 13);
  place(b, 'banner_pole', CAMP.x + 6, CAMP.z + 13);
  lampsAlong(b, toTown, 12, 3.4, { stagger: true, start: 60, model: 'lamp_post' });
  lampsAlong(b, toVale, 12, 3.4, { stagger: true, start: 30, model: 'lamp_post' });
  lampsAlong(b, caravanRd, 14, 3.8, { stagger: true, start: 6, end: 60 });

  // ---------------------------------------------------------------- crossroads
  place(b, 'signpost', 5, 3, -0.7);
  place(b, 'pillar_ruin', -6, -12, 0.4, { scale: 1.2 });
  place(b, 'pillar_ruin', 7, -13, 1.9, { scale: 0.9 });

  // ---------------------------------------------------------------- colonnade ruins & Sandsworn Tomb (north)
  b.flat(RUINS, 18, { layer: 2 });
  for (const row of [-9, 9]) {
    for (let k = 0; k < 5; k++) {
      const x = RUINS.x - 14 + k * 7;
      const z = RUINS.z + row;
      if ((k + (row > 0 ? 1 : 0)) % 4 === 3) continue; // fallen gaps
      place(b, rng.chance(0.4) ? 'pillar_intact' : 'pillar_ruin', x, z, rng.range(0, TAU), { scale: rng.range(1.1, 1.4), tint: 0xe0c8a0 });
    }
  }
  archway(b, { x: 26, z: -56.5 }, Math.atan2(16, -18), { tint: 0xe0c8a0 });
  place(b, 'dungeon_wall', RUINS.x + 16, RUINS.z - 4, 1.4, { tint: 0xd8b890 });
  place(b, 'dungeon_wall', RUINS.x - 17, RUINS.z - 7, -1.2, { tint: 0xd8b890, scale: 0.8 });
  place(b, 'statue_hero', RUINS.x, RUINS.z - 14, 0, { tint: 0xd8c098, scale: 1.6 });
  b.interact({ id: 'ss_lore_colonnade', kind: 'lore', x: RUINS.x - 5, z: RUINS.z - 11, data: { lore: 'sandsworn_edict' } });
  b.interact({ id: 'ss_vista_colonnade', kind: 'vista', x: RUINS.x + 11, z: RUINS.z - 12, data: { name: 'Sunken Colonnade' } });
  b.interact({ id: 'ss_chest_colonnade', kind: 'chest', x: RUINS.x - 13, z: RUINS.z - 14, data: { loot: 'tomb_cache' } });
  b.interact({ id: 'ss_waystone_colonnade', kind: 'waystone', x: 14, z: -50, data: { name: 'Sunken Colonnade' } });
  // Tomb gate cut into the northern cliff.
  b.flat({ x: TOMB.x, z: TOMB.z + 6 }, 10, { layer: 2 });
  b.portal({ id: 'ss_to_tomb', to: 'tomb', toSpawn: 'start', x: TOMB.x, z: TOMB.z, radius: 3, label: 'Sandsworn Tomb', kind: 'dungeon', minLevel: 17 });
  b.spawn('fromTomb', TOMB.x - 2, TOMB.z + 12, 0);
  place(b, 'arch', TOMB.x, TOMB.z - 3.6, 0, { scale: 1.3, tint: 0xd8b890 });
  place(b, 'dungeon_wall', TOMB.x - 8.5, TOMB.z - 3, 0, { tint: 0xd8b890 });
  place(b, 'dungeon_wall', TOMB.x + 8.5, TOMB.z - 3, 0, { tint: 0xd8b890 });
  place(b, 'pillar_intact', TOMB.x - 4.5, TOMB.z + 1, 0, { scale: 1.3, tint: 0xe0c8a0 });
  place(b, 'pillar_intact', TOMB.x + 4.5, TOMB.z + 1, 0, { scale: 1.3, tint: 0xe0c8a0 });
  place(b, 'brazier', TOMB.x - 6, TOMB.z + 5);
  place(b, 'brazier', TOMB.x + 6, TOMB.z + 5);
  place(b, 'sarcophagus', TOMB.x - 10, TOMB.z + 8, 0.2);
  place(b, 'sarcophagus', TOMB.x + 10.5, TOMB.z + 9, -0.3);
  lampsAlong(b, tombRd, 14, 3.4, { stagger: true, start: 50, model: 'brazier' });

  // ---------------------------------------------------------------- oasis & Dunemaw's arena (south)
  for (const p of openSpots(b, 2, { minX: OASIS.x - 20, minZ: OASIS.z - 20, maxX: OASIS.x + 20, maxZ: OASIS.z + 20 }, 10, (p) => dist(p, OASIS) > 12 && dist(p, OASIS) < 17)) {
    b.interact({ id: `ss_fish_${b.interactables.filter((i) => i.kind === 'fish').length}`, kind: 'fish', x: p.x, z: p.z, data: { skill: 'fishing' } });
  }
  b.interact({ id: 'ss_chest_oasis', kind: 'chest', x: OASIS.x - 18, z: OASIS.z - 8, data: { loot: 'oasis_chest' } });
  b.flat(ARENA, 14, { soft: 4 });
  ringOf(b, 'pillar_ruin', ARENA, 16, 14, { phase: 0.1, scale: 1.3, tint: 0xd8b890, skip: (a) => Math.abs(Math.sin(a) + 0.8) < 0.3 || Math.cos(a) < -0.9 });
  for (let i = 0; i < 7; i++) {
    const a = rng.range(0, TAU);
    const r = rng.range(4, 12);
    b.prop('bones', ARENA.x + Math.sin(a) * r, ARENA.z + Math.cos(a) * r, { rot: rng.range(0, TAU), scale: rng.range(0.8, 1.5), noShadow: true });
  }
  b.pack({ id: 'ss_fieldboss', monsters: [{ def: 'dunemaw', count: 1 }], x: ARENA.x, z: ARENA.z, radius: 2, level: 19, respawn: 600, tags: ['fieldBoss'] });

  // ---------------------------------------------------------------- raider camp (south-west)
  b.flat(RAIDERS, 14, { layer: 1 });
  for (const [dx, dz, rot] of [[-8, -6, 0.5], [7, -8, -0.6], [-10, 7, 2.1], [9, 6, -2.4]] as [number, number, number][]) place(b, 'tent', RAIDERS.x + dx, RAIDERS.z + dz, rot, { tint: 0x7a3a2a });
  place(b, 'campfire', RAIDERS.x, RAIDERS.z - 1);
  place(b, 'cart', RAIDERS.x + 12, RAIDERS.z - 1, 1.4, { tint: 0x8a6a4a });
  for (const [dx, dz] of [[3, -11], [4, -12], [-3, 11], [-12, -1]] as [number, number][]) place(b, 'crate', RAIDERS.x + dx, RAIDERS.z + dz, rng.range(0, PI));
  place(b, 'banner_pole', RAIDERS.x - 4, RAIDERS.z - 12, 0, { tint: 0x7a2a2a });
  b.interact({ id: 'ss_chest_raiders', kind: 'chest', x: RAIDERS.x - 6, z: RAIDERS.z + 11, data: { loot: 'raider_chest' } });

  // ---------------------------------------------------------------- east canyon (to Frosthollow)
  b.interact({ id: 'ss_waystone_canyon', kind: 'waystone', x: 90, z: -16, data: { name: 'Scorched Canyon' } });
  b.interact({ id: 'ss_chest_canyon', kind: 'chest', x: 104, z: -38, data: { loot: 'raider_chest' } });
  place(b, 'tent', 76, -36, 0.4, { tint: 0x7a3a2a });
  place(b, 'campfire', 72, -32);
  place(b, 'cart', 66, -34, 2.6, { tint: 0x8a6a4a });

  // South-east badlands.
  b.interact({ id: 'ss_heart', kind: 'heart', x: 98, z: 96, data: { name: 'Heart of the Dunes' } });
  place(b, 'pillar_ruin', 92, 90, 0.3, { scale: 1.4, tint: 0xd8b890 });
  place(b, 'pillar_ruin', 103, 91, 1.1, { scale: 1.1, tint: 0xd8b890 });

  // ---------------------------------------------------------------- monsters (levels climb west → east)
  const pack = (id: string, x: number, z: number, level: number, monsters: [string, number][], radius = 6, respawn = 45) =>
    b.pack({ id, monsters: monsters.map(([def, count]) => ({ def, count })), x, z, radius, level, respawn });
  pack('ss_scorp_a', -66, -80, 12, [['sand_scorpion', 3]]);
  pack('ss_scorp_b', -50, -58, 12, [['sand_scorpion', 4]]);
  pack('ss_scorp_c', -92, 46, 13, [['sand_scorpion', 3]]);
  pack('ss_scorp_d', -72, 34, 13, [['sand_scorpion', 4]]);
  pack('ss_raid_a', RAIDERS.x, RAIDERS.z, 14, [['dune_raider', 3], ['raider_slinger', 2]], 8, 60);
  pack('ss_raid_b', -78, 90, 14, [['dune_raider', 3], ['raider_slinger', 1]], 7, 60);
  pack('ss_raid_c', -44, 94, 15, [['dune_raider', 2], ['raider_slinger', 2]], 7, 60);
  pack('ss_skel_a', -4, -40, 15, [['sunscar_skeleton', 4]]);
  pack('ss_skel_b', RUINS.x + 2, RUINS.z + 2, 16, [['sunscar_skeleton', 5]], 7);
  pack('ss_skel_c', 12, -88, 17, [['sunscar_skeleton', 4], ['sand_golem', 1]], 7, 60);
  pack('ss_skel_d', 64, -80, 17, [['sunscar_skeleton', 5]], 7);
  pack('ss_scorp_e', 24, 20, 16, [['sand_scorpion', 3], ['sand_golem', 1]], 7, 60);
  pack('ss_oasis_a', -22, 62, 17, [['dune_raider', 3], ['raider_slinger', 2]], 7, 60);
  pack('ss_oasis_b', 30, 30, 17, [['sand_scorpion', 4]]);
  pack('ss_bad_a', 78, 44, 18, [['sand_golem', 2], ['sunscar_skeleton', 2]], 7, 90);
  pack('ss_bad_b', 96, 70, 19, [['sunscar_skeleton', 5]], 7);
  pack('ss_bad_c', 72, 96, 20, [['sand_golem', 1], ['sand_scorpion', 3]], 7, 60);
  pack('ss_bad_d', 100, 40, 21, [['sand_golem', 2], ['sunscar_skeleton', 1]], 7, 90);
  pack('ss_ne_a', 82, -84, 18, [['sunscar_skeleton', 4]]);
  pack('ss_ne_b', 96, -94, 19, [['sand_golem', 1], ['sunscar_skeleton', 3]], 7, 60);
  pack('ss_canyon_a', 64, -26, 19, [['dune_raider', 3], ['raider_slinger', 1]], 7, 60);
  pack('ss_canyon_b', 82, -34, 20, [['sand_golem', 2]], 5, 90);
  pack('ss_canyon_c', 102, -38, 21, [['dune_raider', 3], ['raider_slinger', 2]], 7, 60);

  // ---------------------------------------------------------------- gathering & collectibles
  const all = { minX: -100, minZ: -100, maxX: 100, maxZ: 100 };
  const nearOasis = (p: V2) => dist(p, OASIS) > 14 && dist(p, OASIS) < 32;
  lifeNodes(b, 'ss', [
    { kind: 'herb', count: 3, area: all, minDist: 8, accept: nearOasis },
    { kind: 'herb', count: 3, area: all, minDist: 30 },
    { kind: 'timber', count: 3, area: all, minDist: 8, accept: nearOasis },
    { kind: 'timber', count: 2, area: all, minDist: 30 },
    { kind: 'ore', count: 6, area: all, minDist: 22 },
    { kind: 'dig', count: 4, area: all, minDist: 30 },
    { kind: 'seed', count: 10, area: all, minDist: 16 },
  ]);

  // ---------------------------------------------------------------- scenery
  b.forestBorder(
    [{ model: 'cliff_chunk', weight: 3, scale: [1.2, 2.2] }, { model: 'rock_c', weight: 2, scale: [1.1, 1.9] }, { model: 'rock_a', weight: 1, scale: [1, 1.6] }],
    16, 5.5, [W_EXIT, NW_EXIT, E_EXIT],
  );
  const inner = { minX: -102, minZ: -102, maxX: 102, maxZ: 102 };
  const offRoad = (p: V2) => isOpen(b, p, 1);
  const oasisBox = { minX: OASIS.x - 28, minZ: OASIS.z - 28, maxX: OASIS.x + 28, maxZ: OASIS.z + 28 };
  b.scatterProps([{ model: 'tree_palm', weight: 1, collider: { r: 0.5 }, scale: [0.9, 1.35] }], 30, oasisBox, 4.5, (p) => dist(p, OASIS) > 13 && dist(p, OASIS) < 27 && offRoad(p), 1.5);
  b.scatterProps([{ model: 'reeds', weight: 2, scale: [0.9, 1.4], noShadow: true }, { model: 'bush', weight: 1, scale: [0.7, 1.1], noShadow: true }, { model: 'grass_tuft', weight: 2, scale: [0.8, 1.2], noShadow: true }], 50, oasisBox, 2, (p) => dist(p, OASIS) > 11 && dist(p, OASIS) < 19, 0);
  b.scatterProps(
    [
      { model: 'rock_a', weight: 2, collider: { r: 0.9 }, scale: [0.7, 1.6] },
      { model: 'rock_b', weight: 2, collider: { r: 0.9 }, scale: [0.6, 1.4] },
      { model: 'rock_c', weight: 1, collider: { r: 1.2 }, scale: [0.8, 1.5] },
      { model: 'cliff_chunk', weight: 0.5, collider: { r: 2.2 }, scale: [0.8, 1.2] },
    ],
    60, inner, 9, offRoad, 3,
  );
  b.scatterProps(
    [
      { model: 'cactus', weight: 3, collider: { r: 0.5 }, scale: [0.8, 1.4] },
      { model: 'tree_dead', weight: 1, collider: { r: 0.5 }, scale: [0.8, 1.2] },
      { model: 'pillar_ruin', weight: 0.4, collider: { r: 0.8 }, scale: [0.8, 1.2] },
    ],
    70, inner, 7, offRoad, 2,
  );
  b.scatterProps(
    [
      { model: 'bones', weight: 1, noShadow: true, scale: [0.8, 1.3] },
      { model: 'bush', weight: 2, noShadow: true, scale: [0.6, 1] },
      { model: 'grass_tuft', weight: 4, noShadow: true, scale: [0.7, 1.2] },
    ],
    220, inner, 3, (p) => polylineDistance(p, caravanRd) > 3, 0,
  );

  return b.build();
}
